import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { PlayCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { useWizardStore, useWizardActions, useWizardOrgType } from '@/stores';
import { useValidateOrganization, useValidateModules, useModules } from '@/features/migrations/hooks';
import { ValidationProgress, type ModuleValidationState, type ValidationStatus } from '../ValidationProgress';
import { ValidationResults } from '../ValidationResults';
import { OrganizationType } from '@/types';

type Phase = 'idle' | 'validating' | 'complete';

export function ValidationStep() {
  const organizationData = useWizardStore((s) => s.organizationData);
  const selectedModules = useWizardStore((s) => s.selectedModules);
  const organizationType = useWizardOrgType();
  const { setIsValid } = useWizardActions();
  const isNewOrg = organizationType === OrganizationType.New;

  const { data: modulesConfig } = useModules();

  const [phase, setPhase] = useState<Phase>('idle');
  const [moduleStates, setModuleStates] = useState<ModuleValidationState[]>([]);
  const [orgValid, setOrgValid] = useState(false);
  const [orgErrors, setOrgErrors] = useState<string[]>([]);
  const [orgWarnings, setOrgWarnings] = useState<string[]>([]);

  const {
    mutate: validateOrg,
    isPending: orgPending,
  } = useValidateOrganization({
    onSuccess: (result) => {
      setOrgValid(result.isValid);
      setOrgErrors(result.errorMessage ? [result.errorMessage] : []);
      setOrgWarnings([]);
    },
    onError: () => {
      setOrgValid(false);
      setOrgErrors(['Failed to validate organization. Please try again.']);
    },
  });

  const {
    mutate: validateModules,
    isPending: modulesPending,
  } = useValidateModules({
    onSuccess: (response) => {
      const results = response.results ?? [];
      setModuleStates((prev) =>
        prev.map((m) => {
          const result = results.find((r) => r.moduleKey === m.moduleKey);
          if (result) {
            return {
              ...m,
              status: result.isValid ? 'valid' : 'invalid',
              errors: result.blockers?.length
              ? result.blockers
              : result.isValid
              ? []
              : ['Validation failed — the platform returned no details.'],
              warnings: [],
            };
          }
          return { ...m, status: 'invalid' as ValidationStatus, errors: ['No validation result returned'] };
        })
      );
      setPhase('complete');
    },
    onError: () => {
      setModuleStates((prev) =>
        prev.map((m) =>
          m.status === 'validating'
            ? { ...m, status: 'invalid', errors: ['Validation failed unexpectedly'] }
            : m
        )
      );
      setPhase('complete');
    },
  });

  // Initialize module states
  useEffect(() => {
    const states: ModuleValidationState[] = selectedModules.map((mod) => {
      const config = modulesConfig?.find((c) => c.moduleKey === mod.moduleKey);
      return {
        moduleKey: mod.moduleKey,
        moduleName: config?.displayName ?? mod.moduleKey,
        status: 'pending' as ValidationStatus,
        errors: [],
        warnings: [],
      };
    });
    setModuleStates(states);
  }, [selectedModules, modulesConfig]);

  // Check if all validations passed
  useEffect(() => {
    if (phase === 'complete') {
      const allValid = orgValid && moduleStates.every((m) => m.status === 'valid');
      setIsValid(allValid);
    } else {
      setIsValid(false);
    }
  }, [phase, orgValid, moduleStates, setIsValid]);

  const runValidation = useCallback(() => {
    setPhase('validating');
    setOrgErrors([]);
    setOrgWarnings([]);

    // Reset module states to validating
    setModuleStates((prev) =>
      prev.map((m) => ({ ...m, status: 'validating' as ValidationStatus, errors: [], warnings: [] }))
    );

    if (isNewOrg) {
      // New organization: no existing Amplify org to validate — skip org validation entirely
      setOrgValid(true);
    } else if (organizationData) {
      // Existing organization: validate it exists and is reachable in Amplify
      setOrgValid(false);
      validateOrg({
        organizationName: organizationData.organizationName,
        amplifyOrganizationUuid: organizationData.amplifyOrganizationUuid,
      });
    }

    // Validate modules
    validateModules({
      modules: selectedModules.map((m) => ({
        moduleKey: m.moduleKey,
        sourceId: m.sourceId,
      })),
    });
  }, [isNewOrg, organizationData, selectedModules, validateOrg, validateModules]);

  const handleRetry = useCallback(
    (moduleKey: string) => {
      const module = selectedModules.find((m) => m.moduleKey === moduleKey);
      if (module) {
        setModuleStates((prev) =>
          prev.map((m) =>
            m.moduleKey === moduleKey
              ? { ...m, status: 'validating' as ValidationStatus, errors: [], warnings: [] }
              : m
          )
        );
        validateModules({
          modules: [{ moduleKey: module.moduleKey, sourceId: module.sourceId }],
        });
      }
    },
    [selectedModules, validateModules]
  );

  const isValidating = orgPending || modulesPending || phase === 'validating';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Configuration Validation</h3>
          <p className="text-sm text-muted-foreground">
            Verify your organization and module settings before migration.
          </p>
        </div>
        {phase !== 'idle' && (
          <Button
            variant="outline"
            size="sm"
            onClick={runValidation}
            disabled={isValidating}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isValidating ? 'animate-spin' : ''}`} />
            {isValidating ? 'Validating...' : 'Re-validate'}
          </Button>
        )}
      </div>

      {/* Prompt to run validation */}
      {phase === 'idle' && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <ShieldCheck className="h-12 w-12 text-muted-foreground mb-4" />
          <h4 className="font-medium mb-1">Ready to Validate</h4>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            Validate your organization and {selectedModules.length} selected module{selectedModules.length !== 1 ? 's' : ''} against the platform APIs before starting the migration.
          </p>
          <Button onClick={runValidation}>
            <PlayCircle className="h-4 w-4 mr-2" />
            Run Validation
          </Button>
        </div>
      )}

      {/* Show progress while validating */}
      {phase === 'validating' && (
        <ValidationProgress modules={moduleStates} onRetry={handleRetry} />
      )}

      {/* Show results when complete */}
      {phase === 'complete' && (
        <ValidationResults
          modules={moduleStates}
          organizationValid={orgValid}
          organizationErrors={orgErrors}
          organizationWarnings={orgWarnings}
          isNewOrganization={isNewOrg}
        />
      )}
    </div>
  );
}
